import React, { useState } from "react";
import { UploadCloud } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import type { WorkspaceItem } from "../../types";
import { getErrorMessage } from "../../types";

export interface BulkInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  workspaces?: WorkspaceItem[];
  onSuccess: () => Promise<void>;
}

export const BulkInviteModal: React.FC<BulkInviteModalProps> = ({
  isOpen,
  onClose,
  slug,
  workspaces = [],
  onSuccess,
}) => {
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkWorkspaceId, setBulkWorkspaceId] = useState<number | "">("");
  const [bulkStatus, setBulkStatus] = useState<string | null>(null);

  const resetForm = () => {
    setBulkFile(null);
    setBulkWorkspaceId("");
    setBulkStatus(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkFile || !slug) return;
    try {
      setBulkStatus("Uploading and parsing team CSV...");
      const { data, error } = await api.bulkInviteUsersApiV1B2bSlugInvitesBulkPost({
        path: { slug },
        body: {
          file: bulkFile,
          workspace_id: bulkWorkspaceId ? Number(bulkWorkspaceId) : undefined,
        },
      });
      if (data) {
        setBulkStatus(
          `Success: Invited ${(data as any).total_invited} team members! (${(data as any).total_skipped || 0} skipped)`
        );
        setBulkFile(null);
        setTimeout(() => {
          handleClose();
        }, 2000);
        await onSuccess();
      } else {
        setBulkStatus(`Error: ${getErrorMessage(error, "Bulk upload failed")}`);
      }
    } catch (err) {
      setBulkStatus("Error: Network issue uploading CSV");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk CSV Team Import"
      icon={<UploadCloud className="w-5 h-5 text-indigo-600" />}
      maxWidth="max-w-md"
    >
      {bulkStatus && (
        <div
          className={`mb-4 p-3 rounded-xl text-xs font-semibold ${
            bulkStatus.startsWith("Success")
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : bulkStatus.startsWith("Error")
              ? "bg-rose-50 border border-rose-200 text-rose-800"
              : "bg-indigo-50 border border-indigo-200 text-indigo-800"
          }`}
        >
          {bulkStatus}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl">
          <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
            CSV Template Format
          </p>
          <p className="text-slate-600 text-[11px] font-mono leading-relaxed">
            email,role,department,job_title,workspace
          </p>
          <p className="text-slate-500 text-[10px] mt-1">
            Example: alex@domain.com,member,Engineering,Developer,engineering
          </p>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5 flex items-center justify-between">
            <span>Default Target Workspace</span>
            <span className="text-[10px] text-indigo-600 font-normal">Optional</span>
          </label>
          <select
            value={bulkWorkspaceId}
            onChange={(e) => setBulkWorkspaceId(e.target.value ? Number(e.target.value) : "")}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
          >
            <option value="">General (Default Workspace)</option>
            {workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name} ({ws.is_default ? "Default" : `/${ws.slug}`})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-2">Select .CSV File *</label>
          <label className="border-2 border-dashed border-slate-300 hover:border-indigo-500 rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition bg-slate-50">
            <UploadCloud className="w-8 h-8 text-indigo-600 mb-2" />
            <span className="text-slate-700 font-medium">
              {bulkFile ? bulkFile.name : "Click to select or drag & drop CSV"}
            </span>
            {bulkFile && (
              <span className="text-[10px] text-slate-500 mt-1">
                {(bulkFile.size / 1024).toFixed(1)} KB
              </span>
            )}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setBulkFile(e.target.files[0]);
                  setBulkStatus(null);
                }
              }}
            />
          </label>
        </div>

        <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!bulkFile || Boolean(bulkStatus?.includes("Uploading"))}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50"
          >
            {bulkStatus?.includes("Uploading") ? "Uploading..." : "Upload & Invite"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
